import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  DialogActions,
  DialogContent,
} from "@mui/material";
import ResponsiveDialog from "../UI/ResponsiveDialog";
import { useMutation } from "@apollo/client";
import { UPDATE_USER, GET_USERS, GET_USER } from "../../GraphQL/Users";
import useNotification from "../../hooks/useNotification.js";

const EditUserDialog = (props) => {
  const [user, setUser] = useState(props.user);
  const { notifySuccess } = useNotification();

  useEffect(() => {
    setUser(props.user);
  }, [props]);

  const isValid = !!(user?.username?.trim());

  const [updateUser, { loading, error }] = useMutation(UPDATE_USER, {
    variables: {
      id: props.user.id,
      discriminator: user.discriminator,
      discordId: user.discordId,
      username: user.username,
      email: user.email,
      icon: user.icon,
      originId: user.originId,
    },
    refetchQueries: [GET_USERS, GET_USER],
    onCompleted: () => { notifySuccess("User updated."); props.setOpen(false); },
  });

  return (
    <div>
      <ResponsiveDialog
        open={props.open}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isValid) {
            updateUser();
          }
        }}
        title="User"
        onClose={() => props.setOpen(false)}
      >
        <DialogContent>
          <TextField
            data-testid="discriminator"
            autoFocus
            margin="dense"
            id="discriminator"
            label="Discriminator"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={props.user.discriminator}
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 9999) value = 9999;
              if (value < 0) value = 0;
              e.target.value = value;
              setUser({ ...user, discriminator: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 9999 } }}
          />
          <TextField
            data-testid="discordId"
            margin="dense"
            id="discordId"
            label="Discord ID"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={props.user.discordId}
            onChange={(e) => {
              setUser({ ...user, discordId: e.target.value });
            }}
            InputProps={{ inputProps: { maxLength: 50 } }}
          />
          <TextField
            data-testid="username"
            margin="dense"
            id="username"
            label="Username"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={props.user.username}
            onChange={(e) => {
              setUser({ ...user, username: e.target.value });
            }}
            InputProps={{ inputProps: { maxLength: 50 } }}
            helperText={`${user?.username?.length ?? 0}/50`}
          />
          <TextField
            data-testid="email"
            margin="dense"
            id="email"
            label="Email"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={props.user.email}
            onChange={(e) => {
              setUser({ ...user, email: e.target.value });
            }}
            InputProps={{ inputProps: { maxLength: 50 } }}
          />
          <TextField
            data-testid="icon"
            margin="dense"
            id="icon"
            label="Icon"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={props.user.icon}
            onChange={(e) => {
              setUser({ ...user, icon: e.target.value });
            }}
            InputProps={{ inputProps: { maxLength: 50 } }}
          />
          <TextField
            data-testid="originId"
            margin="dense"
            id="originId"
            label="Origin ID"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={props.user.originId}
            onChange={(e) => {
              setUser({ ...user, originId: e.target.value });
            }}
            InputProps={{ inputProps: { maxLength: 50 } }}
          />
        </DialogContent>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error.message}
          </Alert>
        )}
        <DialogActions>
          <Button
            data-testid="saveEditUserDialogButton"
            variant="contained"
            onClick={() => updateUser()}
            disabled={loading || !isValid}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              "Update"
            )}
          </Button>
          <Button
            data-testid="closeEditUserDialogButton"
            onClick={() => props.setOpen(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </div>
  );
};

export default EditUserDialog;
